import { EndpointCallableDefinition } from "./types/client";
import {
    BaseApiError,
    BadRequestError,
    ConflictError,
    ContentTooLargeError,
    ErrorResponseBody,
    ForbiddenError,
    InternalServerError,
    MaintenanceModeError,
    NotFoundError,
    PaymentRequiredError,
    ServiceUnavailableError,
    TooManyRequestsError,
    UnauthorizedError,
    UnprocessableContentError
} from "./types/errors";
import getLogger, { LogGroup, sessionId } from "../../js/core/logger";
import { auth } from "../../js/firebase/auth";
import { withRetry } from "../../common/utils/withRetry";

const logger = getLogger(LogGroup.API);

declare global {
    interface Window { overrideIdToken: string; }
}

interface CallHandlerOptions {
    // eslint-disable-next-line no-undef
    url: RequestInfo;
    // eslint-disable-next-line no-undef
    options: RequestInit;
    idToken: string | undefined;
    apiName: string;
    endpointName: string;
    useLogger: boolean;
}

async function callHandler<Response>({ url, options, idToken, apiName, endpointName, useLogger }: CallHandlerOptions): Promise<Response> {
    // @ts-ignore
    const baseUrl = window.apis[`api-${apiName.toLowerCase()}`];

    // eslint-disable-next-line no-undef
    const headers: HeadersInit = {
        "Content-Type": "application/json",
        "X-Client-Session-Id": sessionId,
        ...(options.headers ?? {}),
    };

    if (idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
    }

    const call = async () => {
        const res = await fetch(`${baseUrl}${url}`, {
            ...options,
            credentials: "same-origin",
            headers
        });

        if (res.status < 400) {
            return res;
        }

        const responseBody: ErrorResponseBody = (await res.json().catch(() => { })) ?? {};
        responseBody.requestId = res.headers.get("X-Request-Id");

        if (res.status === 400) {
            throw new BadRequestError(responseBody.message, responseBody);
        }
        if (res.status === 404) {
            throw new NotFoundError(responseBody.message, responseBody);
        }
        if (res.status === 401) {
            throw new UnauthorizedError(responseBody.message, responseBody);
        }
        if (res.status === 403) {
            throw new ForbiddenError(responseBody.message, responseBody);
        }
        if (res.status === 429) {
            throw new TooManyRequestsError(responseBody.message, responseBody);
        }
        if (res.status === 409) {
            throw new ConflictError(responseBody.message, responseBody);
        }
        if (res.status === 402) {
            throw new PaymentRequiredError(responseBody.message, responseBody);
        }
        if (res.status === 413) {
            throw new ContentTooLargeError(responseBody.message, responseBody);
        }
        if (res.status === 422) {
            throw new UnprocessableContentError(responseBody.message, responseBody);
        }
        if (res.status === 423) {
            throw new MaintenanceModeError(responseBody.message, responseBody);
        }
        if (res.status === 503) {
            throw new ServiceUnavailableError(responseBody.message, responseBody);
        }

        throw new InternalServerError(responseBody.message, responseBody);
    };

    try {
        const res = await withRetry(
            () => call(),
            {
                shouldBackoff: true,
                maxAttempts: 5,
                checkRetryCondition: (error, response) => {
                    const isFailedToFetch = error instanceof Error && error.message.toLowerCase().includes("failed to fetch");

                    return (error as BaseApiError)?.status === 503 ||
                        (error as BaseApiError)?.status === 429 ||
                        isFailedToFetch;
                },
                onBeforeRetry: async (error, response, retriesRemaining: number) => {
                    const logData = {
                        url,
                        method: options.method,
                        status: (error as BaseApiError)?.status,
                        body: options.body,
                        endpointName,
                        apiName,
                        requestId: (error as BaseApiError)?.responseBody?.requestId,
                        retriesRemaining
                    };

                    if (useLogger) {
                        logger.warn(`[callable][${apiName}] request failed, retrying`, logData);
                    } else {
                        console.warn(`[callable][${apiName}] request failed, retrying`, logData);
                    }
                }
            }
        );

        return res.json() as Promise<Response>;
    } catch (err) {
        const apiError = err as BaseApiError | undefined;

        if (apiError?.status === 423 && idToken) {
            // We do it here because we don't want to throw an error, and log the request details
            //  we want to redirect to the maintenance page instead
            window.location.href = "/maintenance";
            return;
        }

        const logData = {
            url,
            method: options.method,
            status: apiError?.status,
            body: options.body,
            requestId: apiError?.responseBody?.requestId,
            endpointName,
            apiName
        };

        if (useLogger) {
            logger.error(err, `[callable][${apiName}] request failed`, logData);
        } else {
            console.error(err, `[callable][${apiName}] request failed`, logData);
        }

        throw err;
    }
}

/**
 * Composes the callable API function from EndpointCallableDefinition
 */
export function getCallable<Request, Response>(apiName: string, endpointName: string, endpointDefinition: EndpointCallableDefinition<Request, Response>, useLogger: boolean = true) {
    /**
     * Callable, typed API function
     */
    const callable = async (request: Request) => {
        let idToken: string | undefined = undefined;
        if (window.overrideIdToken) {
            idToken = window.overrideIdToken;
        } else {
            const currentUser = auth().currentUser;
            if (currentUser) {
                idToken = await currentUser.getIdToken();
            }
        }

        const composed = endpointDefinition.composer(request);
        // eslint-disable-next-line no-undef
        const calls: CallHandlerOptions[] = [];
        if (Array.isArray(composed)) {
            composed.forEach(call => calls.push({ ...call, idToken, apiName, endpointName, useLogger }));
        } else {
            calls.push({ ...composed, idToken, apiName, endpointName, useLogger });
        }

        const responses = await Promise.all(calls.map(call => callHandler<Response>(call)));
        if (responses.length === 1) {
            return responses[0];
        }

        const collector = "collector" in endpointDefinition ? endpointDefinition.collector : undefined;
        if (!collector) {
            throw new Error("Collector is required for batched calls");
        }

        return collector(responses);
    };

    return callable;
}
